// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { cutProductsModalStyles } from "@steiner/App/Components/CutProducts/CutProductsModal";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { mergeWithCss, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

const cutGridTemplate: InjectableCss = {
    css: css`
        display: grid;
        grid-template-columns: repeat(2, 115px 20px) auto;
        ${xScreen("lt", "sm")} {
            grid-template-columns: repeat(2, 75px 20px) auto;
        }
    `,
};

const typographyXSmall: TypographyPresentationProps = {
    size: fontConfig.fontSize.xSmall,
    lineHeight: fontConfig.lineHeight.xSmall,
};

const sharedCutHeaderText: TypographyPresentationProps = {
    ...typographyXSmall,
    weight: fontConfig.weight.bold,
};

cutProductsModalStyles.modal = {
    size: 430,
    cssOverrides: {
        headlineTypography: css`
            line-height: ${fontConfig.lineHeight.small};
        `,
        modalTitle: css`
            padding: 18px 16px 9px;
        `,
        modalContent: css`
            padding: 12px 16px 16px;
        `,
        modalBody: css`
            border-radius: 2px;
            ${xScreen("lte", "md")} {
                max-width: 484px;
            }
            ${xScreen("lt", "xs")} {
                max-width: 100%;
                margin: 0;
            }
        `,
    },
};

cutProductsModalStyles.instructionsText = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
};

cutProductsModalStyles.productWrapper = {
    css: css`
        display: flex;
        gap: 12px;
    `,
};

cutProductsModalStyles.productImage = {
    linkWrappingImage: {
        css: css`
            flex-shrink: 0;
            width: 52px;
            height: 52px;
        `,
    },
    img: {
        width: "52px",
        height: "52px",
        css: css`
            aspect-ratio: 1 / 1;
        `,
    },
};

cutProductsModalStyles.productImageNoPointerStyles = {
    ...mergeWithCss(cutProductsModalStyles.productImage, {
        linkWrappingImage: {
            css: css`
                pointer-events: none;
            `,
        },
    }),
};

cutProductsModalStyles.descriptionWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
    `,
};

cutProductsModalStyles.productTitle = {
    productDetailDescription: {
        ...typographyXSmall,
    },
    productDetailLink: {
        typographyProps: {
            ...typographyXSmall,
        },
    },
};

cutProductsModalStyles.productPartNumbers = {
    container: {
        css: css`
            margin-bottom: 0;
        `,
    },
};

cutProductsModalStyles.cutProductWrapper = {
    css: css`
        display: grid;
        row-gap: 4px;
    `,
};

cutProductsModalStyles.cutHeaderRow = cutGridTemplate;

cutProductsModalStyles.cutHeaderCutsText = {
    ...sharedCutHeaderText,
    css: css`
        grid-column: 1 / 2;
    `,
};

cutProductsModalStyles.cutHeaderFeetText = {
    ...sharedCutHeaderText,
    css: css`
        grid-column: 3 / 4;
    `,
};

cutProductsModalStyles.cutHeaderTotalText = {
    ...sharedCutHeaderText,
    css: css`
        grid-column: 5 / 6;
    `,
};

cutProductsModalStyles.cutItemRow = {
    ...mergeWithCss(cutGridTemplate, {
        css: css`
            ${FormFieldStyle}:last-of-type ${FormInputWrapper} {
                input {
                    padding: 0;
                    border: none;
                    transition: none;
                }
            }
        `,
    }),
};

cutProductsModalStyles.cutTextFieldStyles = {
    mergeCss: true,
    backgroundColor: "transparent",
};

cutProductsModalStyles.cutOperatorStyles = {
    ...sharedCutHeaderText,
    css: css`
        margin: 0 8px;
        height: 34px;
        display: flex;
        align-items: center;
    `,
};

cutProductsModalStyles.disclaimerText = {
    ...typographyXSmall,
    css: css`
        margin-top: 20px;
    `,
};

cutProductsModalStyles.buttonsWrapper = {
    css: css`
        margin-top: 20px;
        display: flex;
        justify-content: space-between;
    `,
};

cutProductsModalStyles.closeButton = {
    variant: "secondary",
};

cutProductsModalStyles.cancelButton = {
    variant: "secondary",
    css: css`
        margin-left: auto;
    `,
};
